import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

const API_BASE = import.meta.env.VITE_API_BASE || "http://192.168.0.57:5000";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Montserrat:wght@300;400;500;600;700&display=swap');

  :root {
    --black:#0a0a0a; --deep:#111111; --card:#161616;
    --border:#2a2a2a; --gold:#c9a84c; --gold-light:#e2c36e;
    --white:#f5f0e8; --grey:#888888; --grey-light:#cccccc;
    --green:#25D366; --red:#e05252; --amber:#e0952a;
  }
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { background:var(--black); color:var(--white); font-family:'Montserrat',sans-serif; font-weight:300; }

  @keyframes fadeUp  { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
  @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
  @keyframes spin    { to{transform:rotate(360deg)} }
  @keyframes pop     { 0%{transform:scale(0.8);opacity:0} 70%{transform:scale(1.05)} 100%{transform:scale(1);opacity:1} }
  @keyframes confetti-fall {
    0%   { transform:translateY(-10px) rotate(0deg); opacity:1; }
    100% { transform:translateY(80px) rotate(360deg); opacity:0; }
  }

  /* ── CONFIRMATION HERO ── */
  .conf-hero {
    background:linear-gradient(160deg,#0d0d0d 0%,#111 60%,#0a0a0a 100%);
    border-bottom:1px solid var(--border);
    padding:100px 60px 48px; position:relative; overflow:hidden;
    text-align:center;
  }
  .conf-hero::before {
    content:''; position:absolute; inset:0;
    background:radial-gradient(ellipse 60% 120% at 50% 50%,rgba(201,168,76,0.08) 0%,transparent 70%);
    pointer-events:none;
  }

  .hero-status-ring {
    width:90px; height:90px; border-radius:50%;
    border:2px solid var(--gold);
    background:rgba(201,168,76,0.08);
    display:flex; align-items:center; justify-content:center;
    font-size:2.2rem; margin:0 auto 20px;
    animation:pop 0.5s ease both;
    position:relative; z-index:1;
  }
  .hero-status-ring.pending {
    border-color:var(--amber); background:rgba(224,149,42,0.08);
  }

  .conf-hero-title {
    font-family:'Cormorant Garamond',serif;
    font-size:clamp(2rem,4vw,3rem); font-weight:300; color:var(--white);
    position:relative; z-index:1;
  }
  .conf-hero-title em { font-style:italic; color:var(--gold); }
  .conf-hero-title.pending em { color:var(--amber); }

  .conf-hero-ref {
    font-size:0.65rem; letter-spacing:0.3em; text-transform:uppercase;
    color:var(--gold); margin:10px 0 6px; position:relative; z-index:1;
  }
  .conf-hero-ref.pending { color:var(--amber); }

  .conf-hero-sub {
    font-size:0.75rem; color:var(--grey); line-height:1.8;
    max-width:500px; margin:0 auto; position:relative; z-index:1;
  }

  .status-badge {
    display:inline-flex; align-items:center; gap:7px;
    padding:6px 18px; margin-top:14px; position:relative; z-index:1;
    font-size:0.58rem; letter-spacing:0.2em; text-transform:uppercase;
  }
  .status-badge.paid {
    background:rgba(37,211,102,0.08); border:1px solid rgba(37,211,102,0.3); color:var(--green);
  }
  .status-badge.pending {
    background:rgba(224,149,42,0.08); border:1px solid rgba(224,149,42,0.35); color:var(--amber);
  }
  .status-dot {
    width:7px; height:7px; border-radius:50%; background:currentColor;
  }

  /* ── BODY ── */
  .conf-body {
    display:grid; grid-template-columns:1fr 320px;
    gap:24px; padding:40px 60px 80px; align-items:start;
  }

  /* ── SECTION CARD ── */
  .rev-card {
    background:var(--card); border:1px solid var(--border);
    position:relative; margin-bottom:20px;
    animation:fadeUp 0.5s ease both;
  }
  .rev-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .rev-card-head {
    padding:16px 24px; border-bottom:1px solid var(--border);
    display:flex; align-items:center; justify-content:space-between;
  }
  .rev-card-title {
    font-size:0.6rem; letter-spacing:0.28em; text-transform:uppercase; color:var(--gold);
  }

  /* review grid */
  .rev-grid {
    display:grid; grid-template-columns:1fr 1fr; gap:0; padding:0;
  }
  .rev-item {
    padding:14px 24px; border-bottom:1px solid var(--border);
    border-right:1px solid var(--border);
  }
  .rev-item:nth-child(even) { border-right:none; }
  .rev-item:nth-last-child(-n+2) { border-bottom:none; }
  .rev-item-label {
    font-size:0.55rem; letter-spacing:0.2em; text-transform:uppercase;
    color:var(--grey); margin-bottom:5px;
  }
  .rev-item-value { font-size:0.8rem; color:var(--white); font-weight:500; }
  .rev-item-value.gold {
    color:var(--gold); font-family:'Cormorant Garamond',serif; font-size:1rem;
  }
  .rev-item-value.green { color:var(--green); }
  .rev-item-value.amber { color:var(--amber); }

  /* car strip */
  .rev-car-strip {
    display:grid; grid-template-columns:160px 1fr auto;
    border-top:1px solid var(--border);
  }
  .rev-car-img { height:110px; background:#111; overflow:hidden; }
  .rev-car-img img {
    width:100%; height:100%; object-fit:contain; padding:10px;
    filter:brightness(0.9);
  }
  .rev-car-info {
    padding:18px 22px; border-left:1px solid var(--border);
    display:flex; flex-direction:column; justify-content:center; gap:8px;
  }
  .rev-car-name {
    font-family:'Cormorant Garamond',serif; font-size:1.3rem; color:var(--white);
  }
  .rev-car-specs { display:flex; gap:18px; }
  .rev-car-spec { font-size:0.68rem; color:var(--grey); display:flex; align-items:center; gap:5px; }
  .rev-car-price {
    padding:18px 22px; border-left:1px solid var(--border);
    display:flex; flex-direction:column; align-items:center;
    justify-content:center; gap:6px; min-width:140px; background:rgba(0,0,0,0.2);
  }
  .rev-badge {
    font-size:0.52rem; letter-spacing:0.2em; text-transform:uppercase;
    padding:3px 10px; border:1px solid rgba(201,168,76,0.3);
    background:rgba(201,168,76,0.07); color:var(--gold);
  }
  .rev-price {
    font-family:'Cormorant Garamond',serif; font-size:1.8rem;
    color:var(--gold); font-weight:300; line-height:1;
  }

  /* ── SIDEBAR ── */
  .conf-sidebar { display:flex; flex-direction:column; gap:16px; }
  .side-card {
    background:var(--card); border:1px solid var(--border); position:relative;
  }
  .side-card::before {
    content:''; position:absolute; top:0; left:0; right:0; height:2px;
    background:linear-gradient(90deg,transparent,var(--gold),transparent);
    background-size:200% 100%; animation:shimmer 3s ease infinite;
  }
  .side-card-head {
    padding:14px 20px; border-bottom:1px solid var(--border);
    font-size:0.6rem; letter-spacing:0.25em; text-transform:uppercase; color:var(--gold);
  }
  .price-rows { padding:8px 0; }
  .price-row {
    display:flex; justify-content:space-between; align-items:center;
    padding:9px 20px; border-bottom:1px solid var(--border); font-size:0.7rem;
  }
  .price-row:last-child { border-bottom:none; }
  .price-row-label { color:var(--grey); }
  .price-row-val { color:var(--white); font-weight:500; }
  .price-row-val.gold {
    font-family:'Cormorant Garamond',serif; font-size:1.1rem; color:var(--gold);
  }
  .price-row-val.big {
    font-family:'Cormorant Garamond',serif; font-size:1.4rem; color:var(--gold);
  }
  .price-row-val.green { color:var(--green); }
  .price-row-val.amber { color:var(--amber); }
  .price-divider { height:1px; background:var(--border); margin:4px 0; }

  /* action buttons */
  .action-btn {
    display:flex; align-items:center; justify-content:center; gap:8px;
    width:100%; padding:14px; font-family:'Montserrat',sans-serif;
    font-size:0.68rem; font-weight:600; letter-spacing:0.2em;
    text-transform:uppercase; cursor:pointer; transition:all 0.3s;
    border:none; text-decoration:none;
  }
  .action-btn.gold-fill {
    background:var(--gold); color:var(--black);
  }
  .action-btn.gold-fill:hover { background:var(--gold-light); }
  .action-btn.outline {
    background:transparent; border:1px solid var(--border); color:var(--grey-light);
  }
  .action-btn.outline:hover { border-color:var(--gold); color:var(--gold); }
  .action-btn.green-outline {
    background:transparent; border:1px solid var(--green); color:var(--green);
  }
  .action-btn.green-outline:hover { background:var(--green); color:var(--black); }
  .action-btn:disabled { opacity:0.4; cursor:not-allowed; }

  /* pending pay-now strip */
  .pending-strip {
    background:rgba(224,149,42,0.06); border:1px solid rgba(224,149,42,0.25);
    padding:16px 20px; margin-bottom:20px; animation:fadeUp 0.5s ease both;
    display:flex; flex-direction:column; gap:10px;
  }
  .pending-strip-title {
    font-size:0.6rem; letter-spacing:0.2em; text-transform:uppercase; color:var(--amber);
    display:flex; align-items:center; gap:8px;
  }
  .pending-strip-msg {
    font-size:0.72rem; color:var(--grey-light); line-height:1.6;
  }

  @media print {
    .conf-sidebar, nav, .conf-hero::before { display:none !important; }
    .conf-body { grid-template-columns:1fr; padding:10px; }
    .rev-card { border:1px solid #ccc; margin-bottom:12px; }
    body { background:#fff; color:#000; }
    .rev-item-value { color:#000; }
    .rev-item-label { color:#666; }
    .rev-car-name { color:#000; }
  }

  @media (max-width:1000px) {
    .conf-body { grid-template-columns:1fr; padding:30px; }
    .conf-hero { padding:100px 30px 36px; }
    .rev-car-strip { grid-template-columns:130px 1fr; }
    .rev-car-price { grid-column:1/-1; border-left:none; border-top:1px solid var(--border); flex-direction:row; justify-content:space-between; }
  }
  @media (max-width:600px) {
    .conf-hero { padding:90px 20px 28px; }
    .conf-body { padding:20px; }
    .rev-grid { grid-template-columns:1fr; }
    .rev-item { border-right:none; }
    .rev-item:nth-last-child(-n+2) { border-bottom:1px solid var(--border); }
    .rev-item:last-child { border-bottom:none; }
    .rev-car-strip { grid-template-columns:1fr; }
  }
`;

export default function TransferConfirmation() {
  const { state }  = useLocation();
  const navigate   = useNavigate();
  const [paying,   setPaying]  = useState(false);
  const [success,  setSuccess] = useState(false);
  useEffect(() => {
    const id = "conf-styles";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id; s.textContent = CSS;
      document.head.appendChild(s);
    }
    return () => document.getElementById(id)?.remove();
  }, []);

  if (!state) { navigate("/"); return null; }

  const {
    pickupLocation, dropLocation, pickupDate, pickupTime,
    returnDate, returnTime, passengers, oneway, finalKm,
    car = {},
    title, firstName, lastName, address, city, postcode,
    country, mobile, email, details,
    arrFlight, arrHour, arrMin,
    depFlight, depHour, depMin, flightDetails,
    total_price,
    bookingRef,
    paymentStatus = "pending",   // "paid" | "pending"
    amountPaid    = 0,
  } = state;

  const currency     = "$";
  const basePrice    = parseFloat(car.base_price)    || 0;
  const ratePerKm    = parseFloat(car.trnsfer_price) || 0;
  const totalKmPrice = parseFloat((finalKm * ratePerKm).toFixed(2));
  const estimated    = parseFloat(total_price) || parseFloat((basePrice + totalKmPrice).toFixed(2));
  const roundPrice   = Math.round(estimated);
  const advance      = parseFloat((roundPrice / 2).toFixed(2));
  const paid         = parseFloat(amountPaid) || 0;
  const remaining    = parseFloat((roundPrice - paid).toFixed(2));
  const isPaid       = paymentStatus === "paid";
  const ref          = bookingRef || "TRF-" + Date.now();

  // Pay later → go to payment page
  const handleGoToPayment = () => {
    navigate("/transfer-payment", { state });
  };

  
  const handlePay = async () => {
    setPaying(true);
    try {
      const res  = await fetch(`${API_BASE}/api/transfer/confirm`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ ...state, confirmed: true }),
      });
      const data = await res.json();
      if (data.success) setSuccess(true);
      else alert(data.message || "Payment failed. Please try again.");
    } catch {
      setSuccess(true);
    } finally {
      setPaying(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <div>
      <Navbar activePage="Book a Taxi" />

      {/* ── HERO ── */}
      <div className="conf-hero">
        <div className={`hero-status-ring ${isPaid ? "" : "pending"}`}>
          {isPaid ? "✓" : "⏱"}
        </div>
        <h1 className={`conf-hero-title ${isPaid ? "" : "pending"}`}>
          {isPaid ? <>Booking <em>Confirmed!</em></> : <>Booking <em>Received!</em></>}
        </h1>
        <div className={`conf-hero-ref ${isPaid ? "" : "pending"}`}>
          Reference · {ref}
        </div>
        <div className="conf-hero-sub">
          {isPaid
            ? <>Thank you, <strong style={{ color:"var(--white)" }}>{firstName}</strong>. A confirmation has been sent to <strong style={{ color:"var(--white)" }}>{email}</strong>. Our driver will be ready for you.</>
            : <>Your booking is saved, <strong style={{ color:"var(--white)" }}>{firstName}</strong>. Complete payment anytime before your trip to guarantee your vehicle. Confirmation sent to <strong style={{ color:"var(--white)" }}>{email}</strong>.</>
          }
        </div>
        <div className={`status-badge ${isPaid ? "paid" : "pending"}`}>
          <div className="status-dot" />
          {isPaid ? "Payment Complete" : "Payment Pending"}
        </div>
      </div>

      <div className="conf-body">
        {/* LEFT */}
        <div>

          {/* Pending payment banner */}
          {!isPaid && (
            <div className="pending-strip">
              <div className="pending-strip-title">⚠ Payment Required</div>
              <div className="pending-strip-msg">
                Your booking is saved but not yet confirmed. Please pay{" "}
                <strong style={{ color:"var(--white)" }}>{currency}{advance.toFixed(2)}</strong> (50% advance)
                to secure your vehicle.
              </div>
              <button className="action-btn gold-fill" onClick={handleGoToPayment}>
                💳 Complete Payment Now
              </button>
            </div>
          )}

          {/* Selected Vehicle */}
          <div className="rev-card">
            <div className="rev-card-head">
              <span className="rev-card-title">Selected Vehicle</span>
            </div>
            <div className="rev-car-strip">
              <div className="rev-car-img">
                <img
                  src={`${API_BASE}/${car.car_image}`}
                  alt={car.car_name}
                  onError={e => { e.target.style.display = "none"; }}
                />
              </div>
              <div className="rev-car-info">
                <div className="rev-car-name">{car.car_name}</div>
                <div className="rev-car-specs">
                  <span className="rev-car-spec">👥 {car.passengers} Passengers</span>
                  <span className="rev-car-spec">🧳 {car.luggage} Luggage</span>
                </div>
              </div>
              <div className="rev-car-price">
                <span className="rev-badge">{oneway === "1" ? "One Way" : "Two Way"}</span>
                <div className="rev-price">{currency}{estimated.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {/* Trip Details */}
          <div className="rev-card">
            <div className="rev-card-head">
              <span className="rev-card-title">Trip Details</span>
            </div>
            <div className="rev-grid">
              {[
                { label:"Pickup Location", value:pickupLocation },
                { label:"Drop Location",   value:dropLocation   },
                { label:"Pickup Date",     value:pickupDate     },
                { label:"Pickup Time",     value:pickupTime     },
                ...(returnDate ? [
                  { label:"Return Date", value:returnDate },
                  { label:"Return Time", value:returnTime },
                ] : []),
                { label:"Passengers", value:passengers },
                { label:"Trip Type",  value:oneway === "1" ? "One Way" : "Two Way" },
                { label:"Distance",   value:`${finalKm} KM` },
              ].map(r => (
                <div className="rev-item" key={r.label}>
                  <div className="rev-item-label">{r.label}</div>
                  <div className="rev-item-value">{r.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Passenger Info */}
          <div className="rev-card">
            <div className="rev-card-head">
              <span className="rev-card-title">Passenger Information</span>
            </div>
            <div className="rev-grid">
              {[
                { label:"Full Name",  value:`${title} ${firstName} ${lastName}` },
                { label:"Email",      value:email   },
                { label:"Mobile",     value:mobile  },
                { label:"Country",    value:country },
                { label:"Address",    value:`${address}${city ? ", " + city : ""}${postcode ? " " + postcode : ""}` },
                { label:"Details",    value:details || "—" },
              ].map(r => (
                <div className="rev-item" key={r.label}>
                  <div className="rev-item-label">{r.label}</div>
                  <div className="rev-item-value">{r.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Flight Details */}
          {(arrFlight || depFlight) && (
            <div className="rev-card">
              <div className="rev-card-head">
                <span className="rev-card-title">Flight Information</span>
              </div>
              <div className="rev-grid">
                {[
                  { label:"Arrival Flight",   value:arrFlight || "—" },
                  { label:"Landing Time",     value:arrFlight ? `${arrHour}:${arrMin}` : "—" },
                  { label:"Departure Flight", value:depFlight || "—" },
                  { label:"Take Off Time",    value:depFlight ? `${depHour}:${depMin}` : "—" },
                  ...(flightDetails ? [{ label:"Flight Notes", value:flightDetails }] : []),
                ].map(r => (
                  <div className="rev-item" key={r.label}>
                    <div className="rev-item-label">{r.label}</div>
                    <div className="rev-item-value">{r.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Payment Details */}
          <div className="rev-card">
            <div className="rev-card-head">
              <span className="rev-card-title">Payment Details</span>
            </div>
            <div className="rev-grid">
              {[
                { label:"Booking Ref",    value:ref,            cls:"gold" },
                { label:"Payment Status", value:isPaid ? "Paid ✓" : "Pending ⏱", cls: isPaid ? "green" : "amber" },
                { label:"Total Price",    value:`${currency}${estimated.toFixed(2)}` },
                { label:"Rounded Price",  value:`${currency}${roundPrice.toFixed(2)}` },
                { label:"Amount Paid",    value:`${currency}${paid.toFixed(2)}`,       cls: isPaid ? "green" : "" },
                { label:"Balance Due",    value:`${currency}${remaining.toFixed(2)}`,  cls: remaining > 0 ? "amber" : "green" },
              ].map(r => (
                <div className="rev-item" key={r.label}>
                  <div className="rev-item-label">{r.label}</div>
                  <div className={`rev-item-value ${r.cls || ""}`}>{r.value}</div>
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* RIGHT SIDEBAR */}
        <div className="conf-sidebar">

          {/* Price Summary */}
          <div className="side-card">
            <div className="side-card-head">Price Summary</div>
            <div className="price-rows">
              <div className="price-row">
                <span className="price-row-label">Base Rate</span>
                <span className="price-row-val">{currency}{basePrice.toFixed(2)}</span>
              </div>
              <div className="price-row">
                <span className="price-row-label">Rate / KM</span>
                <span className="price-row-val">{currency}{ratePerKm.toFixed(2)}</span>
              </div>
              <div className="price-row">
                <span className="price-row-label">Distance</span>
                <span className="price-row-val">{finalKm} KM</span>
              </div>
              <div className="price-row">
                <span className="price-row-label">KM Charges</span>
                <span className="price-row-val">{currency}{totalKmPrice.toFixed(2)}</span>
              </div>
              <div className="price-divider" />
              <div className="price-row">
                <span className="price-row-label">Total</span>
                <span className="price-row-val gold">{currency}{estimated.toFixed(2)}</span>
              </div>
              <div className="price-divider" />
              <div className="price-row">
                <span className="price-row-label">Paid</span>
                <span className={`price-row-val ${isPaid ? "green" : ""}`}>{currency}{paid.toFixed(2)}</span>
              </div>
              <div className="price-row">
                <span className="price-row-label">Balance on Arrival</span>
                <span className={`price-row-val ${remaining > 0 ? "amber" : "green"}`}>{currency}{remaining.toFixed(2)}</span>
              </div>
            </div>

            <div style={{ padding:"0 16px 20px", display:"flex", flexDirection:"column", gap:10 }}>
              <button className="action-btn outline" onClick={handlePrint}>
                🖨 Print Booking
              </button>

              {!isPaid && (
                <button className="action-btn gold-fill" onClick={handleGoToPayment}>
                  💳 Pay Now
                </button>
              )}

        <button className="action-btn outline" onClick={handlePay} disabled={paying}>
                {paying ? (
                  <>
                    <span style={{ width:16, height:16, border:"2px solid #000", borderTopColor:"transparent", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />
                    Processing...
                  </>
                ) : (
                  <> Pay Later</>
                )}
              </button>

              <a href="https://wa.me/255745593330" className="action-btn green-outline">
                <svg width="13" height="13" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Contact via WhatsApp
              </a>

              <button className="action-btn outline" onClick={() => navigate("/")}>
                ← Back to Home
              </button>
            </div>
          </div>

          {/* Booking Ref */}
          <div className="side-card">
            <div className="side-card-head">Booking Reference</div>
            <div style={{ padding:"20px", textAlign:"center" }}>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"1.4rem", color:"var(--gold)", letterSpacing:"0.1em" }}>
                {ref}
              </div>
              <div style={{ fontSize:"0.62rem", color:"var(--grey)", marginTop:6 }}>
                Save this reference number for your records
              </div>
            </div>
          </div>

          {/* What's next */}
          <div className="side-card">
            <div className="side-card-head">What Happens Next</div>
            <div style={{ padding:"16px 0" }}>
              {[
                { icon:"📧", text:isPaid ? "Confirmation email sent to your inbox" : "Booking saved — email confirmation sent" },
                { icon:"📞", text:"Our team will call to confirm details" },
                { icon:"🚗", text:"Driver assigned 24h before pickup" },
                { icon:"✈️", text:"We track your flight for delays" },
                { icon:"💰", text: isPaid ? `Balance of ${currency}${remaining.toFixed(2)} due on arrival` : `Pay advance of ${currency}${advance.toFixed(2)} to confirm` },
              ].map((s, i) => (
                <div key={i} style={{ display:"flex", gap:12, padding:"10px 20px", borderBottom:"1px solid var(--border)", alignItems:"flex-start" }}>
                  <span style={{ fontSize:"0.9rem", flexShrink:0, marginTop:1 }}>{s.icon}</span>
                  <span style={{ fontSize:"0.68rem", color:"var(--grey-light)", lineHeight:1.6 }}>{s.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}